import { inject, Injectable } from '@angular/core';
import { Observable, map, catchError, of, tap } from 'rxjs';
import { QuoteService } from '@quotes/services/quote.service';
import { QuoteListItem } from '@quotes/interfaces/quote-list-item.interface';
import { AuthService } from '@auth/services/auth.service';
import {
  DashboardData,
  DashboardKPIs,
  TrendDataPoint,
  StatusDistribution,
  VehicleTypeDistribution,
  TopProducer,
  RecentQuote
} from '../interfaces/dashboard.interface';
import { QUOTE_CONSTANTS } from '@quotes/constants/quote.constants';

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private readonly quoteService = inject(QuoteService);
  private readonly authService = inject(AuthService);

  /**
   * Obtiene todas las cotizaciones sin paginación para calcular estadísticas
   * No envía quoteTypeCode para obtener cotizaciones de todos los tipos
   */
  private getAllQuotes(): Observable<QuoteListItem[]> {
    // Obtener el producerNumber del usuario autenticado
    const userProducerNumber = this.authService.user()?.producerNumber;
    
    
    // Construir filtros
    // No incluimos quoteTypeCode para que el backend devuelva todas las cotizaciones sin importar el tipo
    const filters: { pageNumber: number; pageSize: number; producerNumber?: number } = {
      pageNumber: 1,
      pageSize: 1000
    };
    
    // Agregar producerNumber si está disponible (para filtrar por usuario)
    if (userProducerNumber) {
      filters.producerNumber = userProducerNumber;
    }
    
    
    // Obtener todas las cotizaciones con un tamaño de página grande
    return this.quoteService.getQuotes(filters).pipe(
      tap(response => {
        if (response?.data && response.data.length > 0) {
        } else {
        }
      }),
      map((response): QuoteListItem[] => {
        if (response && 'data' in response && Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      })
    );
  }

  /**
   * Calcula los KPIs principales del dashboard
   */
  calculateKPIs(quotes: QuoteListItem[]): DashboardKPIs {
    const totalQuotes = quotes.length;
    const approvedQuotes = quotes.filter(q => q.quoteStatus === QUOTE_CONSTANTS.QUOTE_STATUS.ISSUED).length;
    const pendingQuotes = quotes.filter(q => q.quoteStatus === QUOTE_CONSTANTS.QUOTE_STATUS.DRAFT).length;
    const rejectedQuotes = 0; // El API no tiene estado de rechazado, se puede calcular si hay otros estados
    const totalPremiums = quotes.reduce((sum, q) => sum + (q.totalPremium || 0), 0);
    const approvedPremiums = quotes
      .filter(q => q.quoteStatus === QUOTE_CONSTANTS.QUOTE_STATUS.ISSUED)
      .reduce((sum, q) => sum + (q.totalPremium || 0), 0);
    
    const conversionRate = totalQuotes > 0 ? (approvedQuotes / totalQuotes) * 100 : 0;
    const averageValuePerQuote = approvedQuotes > 0 ? approvedPremiums / approvedQuotes : 0;
    const rejectedPercentage = totalQuotes > 0 ? (rejectedQuotes / totalQuotes) * 100 : 0;

    return {
      totalQuotes,
      approvedQuotes,
      pendingQuotes,
      totalPremiums,
      conversionRate,
      averageValuePerQuote,
      rejectedQuotes,
      rejectedPercentage
    };
  }

  /**
   * Calcula datos de tendencia para los últimos 6 meses
   */
  calculateTrendData(quotes: QuoteListItem[]): TrendDataPoint[] {
    const months: { [key: string]: { quotes: number; approved: number } } = {};
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                       'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    
    // Obtener los últimos 6 meses
    const now = new Date();
    const last6Months: Date[] = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      last6Months.push(date);
    }

    // Inicializar meses
    last6Months.forEach(date => {
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months[key] = { quotes: 0, approved: 0 };
    });

    // Procesar cotizaciones
    quotes.forEach(quote => {
      if (quote.workDate) {
        const date = new Date(quote.workDate);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (months[key]) {
          months[key].quotes++;
          if (quote.quoteStatus === QUOTE_CONSTANTS.QUOTE_STATUS.ISSUED) {
            months[key].approved++;
          }
        }
      }
    });

    // Convertir a array de TrendDataPoint
    return last6Months.map(date => {
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const data = months[key] || { quotes: 0, approved: 0 };
      return {
        month: monthNames[date.getMonth()].substring(0, 3), // Primeras 3 letras
        quotes: data.quotes,
        approved: data.approved
      };
    });
  }

  /**
   * Calcula la distribución por estado
   */
  calculateStatusDistribution(quotes: QuoteListItem[]): StatusDistribution[] {
    const total = quotes.length;
    if (total === 0) return [];

    // Contar cotizaciones por estado, incluyendo null/undefined como DRAFT
    const approved = quotes.filter(q => q.quoteStatus === QUOTE_CONSTANTS.QUOTE_STATUS.ISSUED).length;
    const pending = quotes.filter(q => 
      q.quoteStatus === null || 
      q.quoteStatus === undefined || 
      q.quoteStatus === QUOTE_CONSTANTS.QUOTE_STATUS.DRAFT
    ).length;
    const rejected = 0; // El API no tiene estado de rechazado

    const result = [
      {
        label: 'Aprobadas',
        value: approved,
        percentage: total > 0 ? (approved / total) * 100 : 0,
        color: 'success'
      },
      {
        label: 'Pendientes',
        value: pending,
        percentage: total > 0 ? (pending / total) * 100 : 0,
        color: 'warning'
      },
      {
        label: 'Rechazadas',
        value: rejected,
        percentage: total > 0 ? (rejected / total) * 100 : 0,
        color: 'error'
      }
    ].filter(item => item.value > 0); // Solo mostrar estados con valores

    // Debug: Log para verificar los datos
    console.log('[DashboardService] calculateStatusDistribution:', {
      total,
      approved,
      pending,
      rejected,
      result
    });

    return result;
  }

  /**
   * Calcula la distribución por tipo de vehículo
   * Nota: El QuoteListItem no tiene información de tipo de vehículo directamente,
   * por lo que necesitaríamos obtener las cotizaciones completas o usar otro endpoint
   * Por ahora, retornamos datos vacíos o usamos el quoteTypeCode como aproximación
   */
  calculateVehicleTypeDistribution(quotes: QuoteListItem[]): VehicleTypeDistribution[] {
    // Agrupar por quoteTypeCode como aproximación
    const typeMap: { [key: number]: number } = {};
    
    quotes.forEach(quote => {
      typeMap[quote.quoteTypeCode] = (typeMap[quote.quoteTypeCode] || 0) + 1;
    });

    const total = quotes.length;
    const typeNames: { [key: number]: string } = {
      1: 'Autos',
      2: 'Motos',
      3: 'Camiones',
      4: 'Flotas'
    };

    return Object.entries(typeMap)
      .map(([code, count]) => ({
        type: typeNames[Number(code)] || `Tipo ${code}`,
        count,
        percentage: total > 0 ? (count / total) * 100 : 0
      }))
      .sort((a, b) => b.count - a.count);
  }

  /**
   * Calcula los top productores
   */
  calculateTopProducers(quotes: QuoteListItem[]): TopProducer[] {
    const producerMap: { [key: number]: { name: string; quotes: QuoteListItem[] } } = {};

    quotes.forEach(quote => {
      if (!producerMap[quote.producerCode]) {
        producerMap[quote.producerCode] = {
          name: quote.producerName || `Productor ${quote.producerCode}`,
          quotes: []
        };
      }
      producerMap[quote.producerCode].quotes.push(quote);
    });

    return Object.values(producerMap)
      .map(producer => {
        const totalQuotes = producer.quotes.length;
        const approvedQuotes = producer.quotes.filter(
          q => q.quoteStatus === QUOTE_CONSTANTS.QUOTE_STATUS.ISSUED
        ).length;
        const totalPremiums = producer.quotes.reduce(
          (sum, q) => sum + (q.totalPremium || 0), 0
        );

        return {
          producerName: producer.name,
          totalQuotes,
          approvedQuotes,
          totalPremiums
        };
      })
      .sort((a, b) => b.totalQuotes - a.totalQuotes)
      .slice(0, 4); // Top 4
  }

  /**
   * Obtiene las cotizaciones recientes
   */
  getRecentQuotes(quotes: QuoteListItem[]): RecentQuote[] {
    return quotes
      .sort((a, b) => {
        const dateA = a.workDate ? new Date(a.workDate).getTime() : 0;
        const dateB = b.workDate ? new Date(b.workDate).getTime() : 0;
        return dateB - dateA;
      })
      .slice(0, 4) // Mostrar solo 4 según la imagen de referencia
      .map(quote => {
        // Determinar el estado correcto
        let status = 'Pendiente';
        if (quote.quoteStatus === QUOTE_CONSTANTS.QUOTE_STATUS.ISSUED) {
          status = 'Aprobada';
        } else if (quote.quoteStatus === QUOTE_CONSTANTS.QUOTE_STATUS.DRAFT) {
          status = 'Pendiente';
        }
        
        return {
          quoteNumber: quote.quoteNumber,
          clientName: quote.clientName,
          vehicleInfo: quote.quoteTypeName || 'N/A', // Usar el tipo de cotización como info del vehículo
          premium: quote.totalPremium || 0,
          status: status,
          statusCode: quote.quoteStatus,
          date: quote.workDate || ''
        };
      });
  }

  /**
   * Obtiene todos los datos del dashboard
   */
  getDashboardData(): Observable<DashboardData> {
    return this.getAllQuotes().pipe(
      map(quotesArray => {
        console.log('[DashboardService] Total quotes received:', quotesArray.length);
        console.log('[DashboardService] Sample quote statuses:', quotesArray.slice(0, 5).map(q => ({ 
          quoteNumber: q.quoteNumber, 
          status: q.quoteStatus 
        })));
        
        const kpis = this.calculateKPIs(quotesArray);
        const trendData = this.calculateTrendData(quotesArray);
        const statusDistribution = this.calculateStatusDistribution(quotesArray);
        const vehicleTypeDistribution = this.calculateVehicleTypeDistribution(quotesArray);
        const topProducers = this.calculateTopProducers(quotesArray);
        const recentQuotes = this.getRecentQuotes(quotesArray);

        console.log('[DashboardService] Status distribution result:', statusDistribution);

        return {
          kpis,
          trendData,
          statusDistribution,
          vehicleTypeDistribution,
          topProducers,
          recentQuotes
        };
      }),
      catchError(error => {
        // Retornar datos vacíos en caso de error
        return of({
          kpis: {
            totalQuotes: 0,
            approvedQuotes: 0,
            pendingQuotes: 0,
            totalPremiums: 0,
            conversionRate: 0,
            averageValuePerQuote: 0,
            rejectedQuotes: 0,
            rejectedPercentage: 0
          },
          trendData: [],
          statusDistribution: [],
          vehicleTypeDistribution: [],
          topProducers: [],
          recentQuotes: []
        });
      })
    );
  }
}

